import { IGlobalState } from '@/store';
import {
  ICON_BADGE_CHECK,
  IMG_BADGE_AIRPLANE,
  IMG_BADGE_CARD,
  IMG_BADGE_CASH,
  IMG_BADGE_CASH_PICKUP,
  IMG_BADGE_CUSTOM,
  IMG_BADGE_MONEY,
  IMG_CUSTOMS_FORCE_TYPE,
  IMG_TRANSFER,
  IMG_TRANSFER_BADGE,
} from '@repo/assets';
import { getRefundType } from '@repo/constants';
import { format } from 'date-fns';
import styled from 'styled-components';

export const getStatusData = ({
  nationality,
  paymentStatus,
  unapprovedCount,
  isRefundInfoRegistered,
  isUploadRequired,
  isReceiptImageUploaded,
  refundedIn,
  refundedDate,
  isCashPickup,
  isCruise,
}: Partial<IGlobalState>) => {
  let title, description, image;
  let isReceipt,
    isKorReceipt = false;
  if (isUploadRequired) {
    //4009 재외국민
    const currentStep =
      refundedIn !== 'UNKNOWN'
        ? 5
        : Boolean(
              paymentStatus === 'PAYMENT_COMPLETE' &&
                isRefundInfoRegistered &&
                isReceiptImageUploaded,
            )
          ? 4
          : Boolean(isRefundInfoRegistered && isReceiptImageUploaded)
            ? 3
            : isRefundInfoRegistered
              ? 2
              : 1;
    switch (currentStep) {
      case 1:
        if (nationality === 'KOR') {
          title = '송금 정보를 입력하세요';
          description = '아래 버튼을 눌러 송금받을 계좌정보를 입력하세요.';
        } else {
          title = 'Enter the information';
          description =
            'Press the button below to enter\nthe account information to be\nremitted.';
        }
        if (isCashPickup) {
          title = 'Enter your information for Western Union cash pickup';
          description =
            'Please enter your personal information to receive cash via Western Union based on your passport nationality.';
        }
        image = IMG_BADGE_CARD;
        break;
      case 2:
        if (nationality === 'KOR') {
          title = '영수증을 출력한 후\n세관에서 도장을 받고\n업로드하세요.';
          description =
            '환급 영수증을 출력하려면\n‘영수증 출력’ 버튼을 누르세요.';
        } else {
          title = 'Print your receipt,\nstamp it at Customs,\nand upload it.';
          description =
            'Tap the ‘Print Receipts’ button to\nprint your refund receipt.';
        }
        if (isCruise) {
          title = 'Print and submit\nyour refund receipt\nto Customs.';
          description =
            'Submit your printed refund receipt to Customs. Refund will be sent once verified.';
        }
        image = IMG_CUSTOMS_FORCE_TYPE;
        isKorReceipt = true;
        break;
      case 3:
        if (nationality === 'KOR') {
          title = '송금이 준비중입니다';
          description =
            '세관 확인 후, 환급금 송금은\n일반적으로 21영업일 정도 소요됩니다.';
        } else {
          title = 'Your refund is being processed.';
          description =
            'After Customs verification,\nthe refund transfer typically takes\nabout 21 business days.';
        }
        image = IMG_TRANSFER;
        if (isCruise) {
          if (nationality === 'KOR') {
            title = '정보를 확인중입니다';
            description =
              '정보 확인 후, 일반적으로 1-2개월 내에 환급금이 송금됩니다.';
          } else {
            title = 'Your information is being verified';
            description =
              'Refund will be sent within 1-2 months after verifying your submitted information.';
          }

          if (isCashPickup) {
            description =
              'After your information and departure are verified, your refund will be ready for cash pickup at Western Union within 1-2 months';
          }
          image = IMG_TRANSFER_BADGE;
        }
        break;
      case 4:
        if (nationality === 'KOR') {
          title = '송금이 완료되었습니다';
          description = `송금 계좌를 확인해주세요`;
        } else {
          title = 'Your refund has been completed.';
          description = `Please check your bank account.`;
        }
        if (isCruise) {
          if (isCashPickup) {
            title = 'You can now receive your cash';
            description =
              'Visit a Western Union location based on your passport nationality within 3 months of your departure, show the MTCN below, and pick up your refund.';
          }
        }
        image = IMG_BADGE_MONEY;
        break;
      case 5:
        if (nationality === 'KOR') {
          title = '환급 완료되었어요!';
          description = (
            <dl>
              <div style={{ marginBottom: '10px' }}>
                <DataTitle>환급장소</DataTitle>
                <DataValue>
                  {refundedIn && getRefundType(nationality, refundedIn)}
                </DataValue>
              </div>
              <div>
                <DataTitle>환급일시</DataTitle>
                <DataValue>
                  {refundedDate &&
                    format(new Date(refundedDate), 'yyyy-MM-dd HH:mm')}
                </DataValue>
              </div>
            </dl>
          );
        } else {
          title = `Refund Completed`;
          description = (
            <dl>
              <div style={{ marginBottom: '10px' }}>
                <DataTitle>Refund Place</DataTitle>
                <DataValue>
                  {refundedIn && getRefundType(nationality, refundedIn)}
                </DataValue>
              </div>
              <div>
                <DataTitle>Refund Date</DataTitle>
                <DataValue>
                  {refundedDate &&
                    format(new Date(refundedDate), 'yyyy-MM-dd HH:mm')}
                </DataValue>
              </div>
            </dl>
          );
        }
        image = IMG_BADGE_CASH;
        break;
    }
  } else {
    //외국인 + 2년 이상 거주 재외국민
    const currentStep =
      refundedIn !== 'UNKNOWN'
        ? 5
        : Boolean(
              paymentStatus === 'PAYMENT_COMPLETE' &&
                unapprovedCount === 0 &&
                isRefundInfoRegistered,
            )
          ? 4 //즐거운 여행
          : Boolean(unapprovedCount === 0 && isRefundInfoRegistered)
            ? 3 //송금 중
            : Boolean(isRefundInfoRegistered && unapprovedCount !== 0)
              ? 2 //세관 방문
              : 1; //송금 정보

    switch (currentStep) {
      case 1:
        if (nationality === 'KOR') {
          title = '계좌 정보가 필요해요!';
          description = (
            <p>
              환급금을 받을 계좌 정보를{' '}
              <span style={{ color: '#1856CD' }}>3개월 이내</span>에
              <br />
              입력해주세요.
            </p>
          );
        } else if (isCashPickup) {
          title = (
            <>
              Enter your information for
              <br />
              Western Union cash pickup
            </>
          );
          description = (
            <>
              Please enter your personal
              <br />
              information to receive cash via
              <br />
              Western Union.
            </>
          );
        } else {
          title = 'Enter the information.';
          description = (
            <p>
              You must enter the information
              <br />
              <span style={{ color: '#1856CD' }}>within 3 months</span> for the
              procedure
              <br />
              to receive a refund.
            </p>
          );
        }
        image = IMG_BADGE_CARD;
        break;
      case 2:
        if (nationality === 'KOR') {
          title = '세관을 방문해주세요!';
          description = `세관 방문 후 하단 버튼을 클릭하여 모바일 Tax Refund 영수증과 구매한 물건을 보여주세요.`;
        } else if (isCashPickup) {
          title = 'Visit customs';
          description = `After customs, click the button below to show your receipt and items.`;
        } else {
          title = 'Visit customs';
          description = `After visiting customs, click the button below to show your mobile tax refund receipt and your purchase to custom officers.`;
        }
        image = IMG_BADGE_CUSTOM;
        isReceipt = true;
        break;
      case 3:
        if (nationality === 'KOR') {
          title = '환급액을 송금 중입니다!';
          description =
            '텍스리펀은 세관 확인 후 송금까지\n영업일 기준 평균 21일 소요됩니다.';
        } else if (isCashPickup) {
          title = (
            <>
              Your information
              <br />
              is being verified
            </>
          );
          description = (
            <>
              After your information and departure are verified, your refund
              will be ready for cash pickup at Western Union{' '}
              <Strong>within 21 days</Strong>
            </>
          );
        } else {
          title = `Transferring your refund`;
          description = `Tax refund will be completed about\n21 business days after customs\nconfirmation.`;
        }
        image = IMG_TRANSFER_BADGE;
        break;
      case 4:
        if (nationality === 'KOR') {
          title = '환급금을 송금했어요!';
          description = `즐거운 여행 되셨길 바랍니다`;
          image = IMG_BADGE_AIRPLANE;
        } else if (isCashPickup) {
          title = (
            <TitleWrapper>
              <CheckIcon src={ICON_BADGE_CHECK} alt='check' />
              You can now receive your cash
            </TitleWrapper>
          );
          description = (
            <div>
              <Contents>
                Visit a Western Union location <br />
                <Strong>within 3 months of your departure</Strong>, show the
                MTCN below, and pick up your refund.
              </Contents>
            </div>
          );
          image = IMG_BADGE_CASH_PICKUP;
        } else {
          title = `Remittance Completed`;
          description = `We hope you had a great trip`;
          image = IMG_BADGE_AIRPLANE;
        }
        break;
      case 5:
        if (nationality === 'KOR') {
          title = '환급 완료되었어요!';
          description = (
            <dl>
              <div style={{ marginBottom: '10px' }}>
                <DataTitle>환급장소</DataTitle>
                <DataValue>
                  {refundedIn && getRefundType(nationality, refundedIn)}
                </DataValue>
              </div>
              <div>
                <DataTitle>환급일시</DataTitle>
                <DataValue>
                  {refundedDate &&
                    format(new Date(refundedDate), 'yyyy-MM-dd HH:mm')}
                </DataValue>
              </div>
            </dl>
          );
        } else {
          title = `Refund Completed`;
          description = (
            <dl>
              <div style={{ marginBottom: '10px' }}>
                <DataTitle>Refund Place</DataTitle>
                <DataValue>
                  {refundedIn && getRefundType(nationality, refundedIn)}
                </DataValue>
              </div>
              <div>
                <DataTitle>Refund Date</DataTitle>
                <DataValue>
                  {refundedDate &&
                    format(new Date(refundedDate), 'yyyy-MM-dd HH:mm')}
                </DataValue>
              </div>
            </dl>
          );
        }
        image = IMG_BADGE_CASH;
        break;
    }
  }
  return { title, description, image, isReceipt, isKorReceipt };
};

const DataTitle = styled.dt`
  color: #999;
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.28px;
`;

const DataValue = styled.dd`
  color: #111;
  font-size: 16px;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: -0.32px;
`;

const Contents = styled.p`
  color: var(--KTP-Grayscale-50, var(--KTP-Color-Mono-50, #80848a));
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.28px;
`;

const Strong = styled.strong`
  color: var(--KTP-Warning-100, #ed0828);
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.28px;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CheckIcon = styled.img`
  width: 24px;
  height: 24px;
`;
